// Wire format of sidecar/native/src/pipeline/devtools_channel.rs: a MORE or LAST byte, then part
// of the frame. The sidecar accepts a channel with a lone OPENED byte and refuses it by closing it.

import * as Port from "../../devtools/port";

const LABEL = "devtools";

const MORE = 0;
const LAST = 1;
const OPENED = 2;

// Chrome's max-message-size.
const MAX_MESSAGE_BYTES = 256 * 1024;
// RFC 8841 default, used until the size is known.
const DEFAULT_MESSAGE_BYTES = 64 * 1024;
const FRONTEND_POLL_MS = 1000;

const encoder = new TextEncoder();

// sctp.maxMessageSize reads Infinity until it is negotiated.
export function fragmentBytes(maxMessageSize) {
  const messageBytes =
    Number.isFinite(maxMessageSize) && maxMessageSize > 1
      ? Math.min(maxMessageSize, MAX_MESSAGE_BYTES)
      : DEFAULT_MESSAGE_BYTES;

  return messageBytes - 1;
}

export function fragments(frame, size) {
  const bytes = encoder.encode(frame);
  const count = Math.max(1, Math.ceil(bytes.length / size));

  return Array.from({ length: count }, (_, index) => {
    const payload = bytes.subarray(index * size, (index + 1) * size);
    const fragment = new Uint8Array(payload.length + 1);
    fragment[0] = index === count - 1 ? LAST : MORE;
    fragment.set(payload, 1);
    return fragment;
  });
}

export class FrameAssembler {
  constructor() {
    this.decoder = new TextDecoder();
    this.pending = "";
  }

  push(fragment) {
    const payload = fragment.subarray(1);

    if (fragment[0] === MORE) {
      this.pending += this.decoder.decode(payload, { stream: true });
      return null;
    }

    const frame = this.pending + this.decoder.decode(payload);
    this.pending = "";
    return frame;
  }
}

export function serveDevTools(getPeer, getSessionId) {
  const connections = new Set();

  const api = {
    connect(frontend, id, sessionId) {
      const peer = getPeer();
      if (sessionId !== getSessionId()) return false;
      if (!peer || ["closed", "failed"].includes(peer.connectionState)) return false;

      const connection = relay(peer, frontend, id, () => connections.delete(connection));
      connections.add(connection);
      return true;
    },
  };

  const closeAll = () => connections.forEach((connection) => connection.close());

  window.sauronDevTools = api;
  // A popped-out frontend outlives this page and its peer.
  window.addEventListener("pagehide", closeAll);

  return {
    closeAll,
    stop() {
      window.removeEventListener("pagehide", closeAll);
      closeAll();
      if (window.sauronDevTools === api) delete window.sauronDevTools;
    },
  };
}

function relay(peer, frontend, id, forget) {
  const channel = peer.createDataChannel(LABEL);
  const { port1: port, port2 } = new MessageChannel();
  const assembler = new FrameAssembler();
  let closed = false;

  const close = () => {
    if (closed) return;
    closed = true;
    clearInterval(watch);
    forget();
    port.postMessage({ type: Port.CLOSED });
    port.close();
    channel.close();
  };

  channel.binaryType = "arraybuffer";

  channel.onmessage = ({ data }) => {
    const fragment = new Uint8Array(data);

    if (fragment.length === 1 && fragment[0] === OPENED) {
      port.postMessage({ type: Port.OPENED });
    } else if (fragment[0] === MORE || fragment[0] === LAST) {
      const frame = assembler.push(fragment);
      if (frame !== null) port.postMessage({ type: Port.FRAME, data: frame });
    } else {
      close();
    }
  };

  channel.onclose = close;

  port.onmessage = ({ data }) => {
    if (data.type === Port.CLOSE) {
      close();
    } else if (data.type === Port.FRAME && channel.readyState === "open") {
      // A refused fragment would leave the sidecar with half a frame.
      try {
        const size = fragmentBytes(peer.sctp?.maxMessageSize);
        fragments(data.data, size).forEach((part) => channel.send(part));
      } catch {
        close();
      }
    }
  };

  // A removed iframe or a closed popup may never send CLOSE.
  const watch = setInterval(() => frontend.closed && close(), FRONTEND_POLL_MS);

  frontend.postMessage({ type: Port.PORT_MESSAGE, id }, window.location.origin, [port2]);

  return { close };
}
